import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Star, Send, IndianRupee, BarChart3, MessageSquare, ChevronLeft, Receipt,
  TrendingUp, Calendar, Sparkles, CheckCircle2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { RatingBadge } from '../RatingBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const formatDate = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } };

const SectionTitle = ({ title, subtitle, action }) => (
  <div className="flex items-start justify-between gap-4 mb-6">
    <div>
      <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-black">{title}</h2>
      {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
    </div>
    {action}
  </div>
);

// ========================================================================
// REVIEWS
// ========================================================================
export const ReviewsSection = ({ ratings, reloadRatings }) => {
  const [replyDraft, setReplyDraft] = useState({});

  const replyToReview = async (rid) => {
    const text = (replyDraft[rid] || '').trim();
    if (!text) return;
    try {
      await axios.post(`${API}/hotel/ratings/${rid}/reply`, { reply: text }, { withCredentials: true });
      toast.success('Reply posted');
      setReplyDraft({ ...replyDraft, [rid]: '' });
      reloadRatings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="reviews-section">
      <SectionTitle title="Guest Reviews" subtitle={`${ratings.length} review${ratings.length === 1 ? '' : 's'}`} />
      {ratings.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <Star className="h-10 w-10 mx-auto text-gray-200 mb-3" />
          <p className="text-sm text-gray-500">No reviews yet. They'll appear here once guests check out and rate their stay.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {ratings.map((r) => (
            <div key={r._id} className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={`review-card-${r._id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <p className="font-bold text-black">{r.guest_name || 'Guest'}</p>
                  <p className="text-xs text-gray-500">{formatDate(r.created_at)}</p>
                </div>
                <div className="flex items-center gap-0.5">
                  <RatingBadge value={r.rating} size="sm" showLabel={false} testId={`hadmin-rate-${r._id}`} />
                </div>
              </div>
              {r.comment && <p className="text-sm text-gray-700 mt-2">{r.comment}</p>}
              {r.reply ? (
                <div className="mt-3 pl-3 border-l-2 border-[#FF0059] bg-[#FFE5EE]/30 p-3 rounded-r-lg" data-testid={`review-reply-${r._id}`}>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[#CC0047] mb-1">Hotel reply</p>
                  <p className="text-sm text-gray-800">{r.reply}</p>
                </div>
              ) : (
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Write a reply…"
                    value={replyDraft[r._id] || ''}
                    onChange={(e) => setReplyDraft({ ...replyDraft, [r._id]: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') replyToReview(r._id); }}
                    className="flex-1 px-3 py-2 rounded-full border border-gray-200 focus:border-[#FF0059] outline-none text-sm"
                    data-testid={`review-reply-input-${r._id}`}
                  />
                  <button onClick={() => replyToReview(r._id)} className="px-4 py-2 rounded-full bg-[#FF0059] hover:bg-[#CC0047] text-white text-xs font-bold inline-flex items-center gap-1" data-testid={`review-reply-send-${r._id}`}>
                    <Send className="h-3 w-3" /> Reply
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ========================================================================
// FINANCE
// ========================================================================
export const FinanceSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/hotel/finance`, { withCredentials: true });
      setData(res);
    } catch (e) { toast.error('Failed to load finance'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) return <p className="text-sm text-gray-500">Loading finance…</p>;
  const { totals, monthly } = data;
  const Tile = ({ label, value, sub }) => (
    <div className="bg-white border border-gray-200 rounded-2xl p-5">
      <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">{label}</p>
      <p className="text-2xl font-black text-black mt-1 inline-flex items-center">
        <IndianRupee className="h-5 w-5" />{(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
      </p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );

  return (
    <div data-testid="finance-section">
      <SectionTitle title="Finance" subtitle="Settle commissions and track payouts in one place." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Tile label="Gross revenue" value={totals.total_revenue} sub={`${totals.total_bookings} bookings`} />
        <Tile label="Commission owed" value={totals.total_commission} sub="13% standard rate" />
        <Tile label="Net payout" value={totals.total_payout} sub="cumulative" />
        <Tile label="Bookings settled" value={totals.total_bookings} sub="all time" />
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="finance-monthly-table">
        <div className="grid grid-cols-5 gap-3 px-5 py-3 bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500">
          <p>Month</p><p>Bookings</p><p>Gross</p><p>Commission</p><p className="text-right">Net payout</p>
        </div>
        {monthly.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8">No revenue yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {monthly.map((m) => (
              <li key={m.month} className="grid grid-cols-5 gap-3 px-5 py-3 text-sm" data-testid={`finance-month-${m.month}`}>
                <p className="font-bold text-black">{m.month}</p>
                <p className="text-gray-700">{m.bookings}</p>
                <p className="text-gray-700">{formatINR(m.revenue)}</p>
                <p className="text-red-600">- {formatINR(m.commission)}</p>
                <p className="text-right font-black text-[#FF0059]">{formatINR(m.net_payout)}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ========================================================================
// INSIGHTS
// ========================================================================
export const InsightsSection = ({ insights }) => {
  if (!insights) return <p className="text-sm text-gray-500">Loading insights…</p>;
  const { totals, series_14d, rating_distribution, status_breakdown } = insights;
  const maxSeries = Math.max(1, ...series_14d.map((d) => d.count));
  const maxRating = Math.max(1, ...Object.values(rating_distribution));

  return (
    <div data-testid="insights-section">
      <SectionTitle title="Insights" subtitle="How your property is performing — last 30 days." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Bookings · 30d</p>
          <p className="text-2xl font-black text-black mt-1">{totals.bookings_30d}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Revenue · 30d</p>
          <p className="text-2xl font-black text-black mt-1">{formatINR(totals.revenue_30d)}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Avg. rating</p>
          <p className="text-2xl font-black text-black mt-1">{Number(totals.avg_rating).toFixed(1)}<span className="text-base text-gray-400"> /10</span></p>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Reviews</p>
          <p className="text-2xl font-black text-black mt-1">{totals.review_count}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="insights-trend">
          <h3 className="font-black text-black text-lg mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#FF0059]" /> Bookings · last 14 days</h3>
          <div className="flex items-end gap-1.5 h-32 mt-4">
            {series_14d.map((d) => {
              const h = (d.count / maxSeries) * 100;
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${d.date}: ${d.count}`}>
                  <div className="w-full bg-[#FF0059]/15 rounded-t-md flex-1 flex items-end">
                    <div className="w-full bg-[#FF0059] rounded-t-md transition-all" style={{ height: `${h}%` }} />
                  </div>
                  <span className="text-[9px] text-gray-400">{d.date.slice(-2)}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="insights-rating-dist">
          <h3 className="font-black text-black text-lg mb-3 flex items-center gap-2"><Star className="h-4 w-4 text-[#FFCB00]" /> Rating distribution (out of 10)</h3>
          {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map((s) => {
            const count = rating_distribution[s] || rating_distribution[String(s)] || 0;
            const pct = (count / maxRating) * 100;
            return (
              <div key={s} className="flex items-center gap-2 text-sm mb-1.5">
                <span className="w-5 text-right text-xs font-black tabular-nums">{s}</span>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#FFCB00]" style={{ width: `${pct}%` }} />
                </div>
                <span className="w-10 text-right text-gray-500 text-xs">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5 mt-5" data-testid="insights-status">
        <h3 className="font-black text-black text-lg mb-3">Booking status breakdown</h3>
        <div className="flex flex-wrap gap-3">
          {Object.entries(status_breakdown).map(([s, n]) => (
            <div key={s} className="px-4 py-2 rounded-full bg-gray-50 border border-gray-200">
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">{s.replace('_', ' ')}</span>
              <span className="ml-2 font-black text-black">{n}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ========================================================================
// INBOX (poll-based)
// ========================================================================
export const InboxSection = () => {
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState('');
  const pollRef = useRef(null);
  const bottomRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/hotel/inbox`, { withCredentials: true });
      setThreads(data);
    } catch (err) { console.error('[Inbox] loadThreads failed:', err); }
  }, []);

  const loadThread = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await axios.get(`${API}/hotel/inbox/${id}`, { withCredentials: true });
      setActive(data);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (err) { console.error('[Inbox] loadThread failed:', err); }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  useEffect(() => {
    if (!activeId) return undefined;
    loadThread(activeId);
    pollRef.current = setInterval(() => loadThread(activeId), 10000);
    return () => clearInterval(pollRef.current);
  }, [activeId, loadThread]);

  const send = async () => {
    if (!draft.trim() || !activeId) return;
    try {
      await axios.post(`${API}/hotel/inbox/${activeId}`, { booking_id: activeId, text: draft }, { withCredentials: true });
      setDraft('');
      loadThread(activeId);
      loadThreads();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="inbox-section">
      <SectionTitle title="Inbox" subtitle="Chat with your guests directly. New messages refresh every 10 seconds." />
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden grid lg:grid-cols-3 min-h-[60vh]">
        {/* threads list */}
        <div className={`lg:col-span-1 lg:border-r border-gray-100 ${activeId ? 'hidden lg:block' : 'block'}`} data-testid="inbox-threads">
          {threads.length === 0 ? (
            <p className="p-8 text-center text-sm text-gray-500">No conversations yet. Once a guest messages you, it appears here.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {threads.map((t) => (
                <li key={t.booking_id}>
                  <button
                    onClick={() => setActiveId(t.booking_id)}
                    className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${activeId === t.booking_id ? 'bg-[#FFE5EE]' : ''}`}
                    data-testid={`inbox-thread-${t.booking_id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="font-bold text-black truncate">{t.guest_name || 'Guest'}</p>
                      {t.unread > 0 && (
                        <span className="text-[10px] font-black bg-[#FF0059] text-white rounded-full px-1.5 py-0.5">{t.unread}</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 truncate">{t.latest_text || `${t.check_in} → ${t.check_out}`}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* messages pane */}
        <div className={`lg:col-span-2 flex flex-col ${activeId ? 'flex' : 'hidden lg:flex'}`} data-testid="inbox-messages">
          {!active ? (
            <div className="flex-1 flex items-center justify-center p-10 text-center text-sm text-gray-500">
              <div>
                <MessageSquare className="h-10 w-10 mx-auto text-gray-200 mb-3" />
                Select a conversation to view messages.
              </div>
            </div>
          ) : (
            <>
              <div className="border-b border-gray-100 p-4 flex items-center gap-3">
                <button onClick={() => setActiveId(null)} className="lg:hidden p-1 rounded-full hover:bg-gray-100">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div>
                  <p className="font-black text-black">{active.guest_name || 'Guest'}</p>
                  <p className="text-xs text-gray-400">Booking #{(active.booking_id || '').slice(-8)}</p>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#FAFAFA] max-h-[50vh]">
                {active.messages.length === 0 ? (
                  <p className="text-center text-xs text-gray-400 py-8">No messages yet. Say hi to your guest 👋</p>
                ) : (
                  active.messages.map((m) => (
                    <div key={m._id} className={`flex ${m.from_role === 'hotel' ? 'justify-end' : 'justify-start'}`} data-testid={`inbox-message-${m._id}`}>
                      <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${m.from_role === 'hotel' ? 'bg-[#FF0059] text-white rounded-br-sm' : 'bg-white border border-gray-200 text-black rounded-bl-sm'}`}>
                        <p>{m.text}</p>
                        <p className={`text-[10px] mt-1 ${m.from_role === 'hotel' ? 'text-white/70' : 'text-gray-400'}`}>{new Date(m.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={bottomRef} />
              </div>

              <div className="border-t border-gray-100 p-3 flex items-center gap-2">
                <input
                  type="text"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                  placeholder="Type a message…"
                  className="flex-1 px-4 py-2 rounded-full border border-gray-200 focus:border-[#FF0059] outline-none text-sm"
                  data-testid="inbox-draft-input"
                />
                <button onClick={send} className="px-4 py-2 rounded-full bg-[#FF0059] hover:bg-[#CC0047] text-white text-sm font-bold inline-flex items-center gap-1" data-testid="inbox-send-btn">
                  <Send className="h-3.5 w-3.5" /> Send
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
